import * as React from 'react';
import { useNavigate } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { libraryLink, type FilterLink } from '@chess/api-insights/charts';
import { StageErrorsAggregator } from '@chess/api-insights';
import { registerAggregator, useAggregate } from '@chess/api-model/react';
import { registerCard } from '@chess/api-dashboard';
import '@chess/api-insights/charts.css';
import { MIDDLEGAME_PIECES_CARD, MiddlegamePiecesCard } from './MiddlegamePiecesCard.tsx';

// Middlegame card "Blunders by piece lost": reads the middlegame slice of the
// game store and renders `MiddlegamePiecesCard`.
// On `middlegame.more` (order 30): the Middlegame screen's "More" section,
// under the stage's top cards; not on Home, which shows the top cards only.
// Its links open the library filtered by `blunder` (`libraryLink`); the
// library, like the card, covers the last 30 days.
export function setup(app: PiletApi) {
  const getApi = <T,>(name: string) => app.getApi<T>(name);
  // The stage's errors, shared with the other middlegame cards: the service runs it once.
  const aggregator = new StageErrorsAggregator('middlegame');
  registerAggregator(getApi, aggregator);
  const Card: React.FC = () => {
    const { result, state } = useAggregate(getApi, aggregator);
    const navigate = useNavigate();
    const link = React.useMemo<FilterLink>(() => libraryLink((path) => navigate(path), 'middlegame'), [navigate]);
    // Never hidden while the store loads: the card shows skeletons for its values.
    if (state?.status !== 'ready') return state?.status === 'failed' ? null : <MiddlegamePiecesCard loading />;
    return result ? <MiddlegamePiecesCard middlegame={result} link={link} /> : null;
  };
  registerCard(app, 'middlegame.more', Card, { order: 30, ...MIDDLEGAME_PIECES_CARD });
}
